import { LadiesMain } from "../../global/ladies-main"
import { InfoPanel } from "../../info-panel/InfoPanel"
import { openGenericModal } from "../../util/generic-modal"

export default class StarsPostsLikes {
    constructor(/** jQuery */ jQuery) {
        this.$ = jQuery
        this.likesApiUrl = "/api/stars-post-likes/"
        this.starsSeparationLikesApiUrl = SiteInfo.starsApiUrl + "/api/post-likes-http-api/command/toggle-like"
    }

    listenForLikesInteraction(/** HTMLElement */ post) {
        // Listen for click on post itself in case loading
        // was not yet complete (for dynamically added posts)
        post.addEventListener(
            "click",
            (clickEvent) => {
                const likeButton = clickEvent.target.closest(".like-unlike-button")

                if (likeButton === null) {
                    // Jump out if click on post was not triggered by like-button
                    return
                }

                if (this.isUnableToChangeLikeState(likeButton)) {
                    return
                }

                clickEvent.stopPropagation()

                if (likeButton.hasAttribute("disabled")) {
                    return Promise.reject(new Error())
                }

                likeButton.disabled = true

                let isLike = true

                if (likeButton.dataset.liked === "true") {
                    isLike = false
                }

                this.addLikeInStarsSeparation(likeButton.dataset.postId, isLike).then(
                    this.updateLikeButtonStateInStarsSeparation(likeButton, isLike),
                )
            },
            {
                // Passive makes sure the event doesn't affect performance
                passive: true,
            },
        )
    }

    async addLikeInStarsSeparation(/** string */ postId, /** boolean */ isLike) {
        await fetch(this.starsSeparationLikesApiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${userData.accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                postId: postId,
                toggleTo: isLike,
            }),
        })
    }

    addLikeEventToQueue(/** HTMLElement */ likeButton) {
        /** Promise */
        if (likeButton.hasAttribute("disabled")) {
            return Promise.reject(new Error())
        }

        likeButton.disabled = true

        let likeRequestType = "addLike"

        if (likeButton.dataset.liked === "true") {
            likeRequestType = "revokeLike"
        }

        return fetch(SiteInfo.yiiLadiesUrl + this.likesApiUrl + likeRequestType, {
            method: "POST",
            body: this.preparePostBody(likeButton),
            credentials: "include",
        })
    }

    async updateLikeButtonState(/** HTMLElement */ likeButton, /** Response */ result) {
        /** void */
        const response = await result.json()

        const postContainer = likeButton.closest(".stars-post")
        const creatorData = postContainer.querySelector("data[data-owner-id]")

        if (!("ownerId" in creatorData.dataset)) {
            throw new Error(
                "Etwas ist beim Auslesen der ID des Posts schiefgegangen. Bitte lade die Seite neu und versuche es noch einmal.",
            )
        }

        if (result.status !== 200) {
            throw new Error(
                this.mapErrorResponse(
                    document.querySelector(`.creatorProfileName[data-creator-id='${creatorData.dataset.ownerId}']`)
                        .dataset.creatorName,
                    response,
                ),
            )
        }

        if (response.type === "AddPostLikeCommand") {
            this.changeStateToLiked(likeButton)
        } else if (response.type === "RevokePostLikeCommand") {
            this.changeStateToUnliked(likeButton)
        } else {
            throw new Error("unknown type")
        }
    }

    async updateLikeButtonStateInStarsSeparation(/** HTMLElement */ likeButton, /** Boolean */ isLike) {
        /** void */

        const postContainer = likeButton.closest(".stars-post")
        const creatorData = postContainer.querySelector("data[data-owner-id]")

        if (!("ownerId" in creatorData.dataset)) {
            throw new Error(
                "Etwas ist beim Auslesen der ID des Posts schiefgegangen. Bitte lade die Seite neu und versuche es noch einmal.",
            )
        }

        if (isLike) {
            this.changeStateToLiked(likeButton)
        } else {
            this.changeStateToUnliked(likeButton)
        }
    }

    changeStateToLiked(/** HTMLElement */ likeButton) {
        const likeCounter = likeButton.querySelector(".like-count")

        likeButton.setAttribute("data-liked", "true")
        likeButton.disabled = false

        if (!likeCounter) {
            return
        }

        if (Number.isNaN(Number.parseInt(likeCounter.innerText, 10))) {
            likeCounter.innerText = 0
        }

        likeCounter.innerText = Number.parseInt(likeCounter.innerText, 10) + 1
    }

    changeStateToUnliked(/** HTMLElement */ likeButton) {
        const likeCounter = likeButton.querySelector(".like-count")

        likeButton.setAttribute("data-liked", "false")
        likeButton.disabled = false

        if (!likeCounter) {
            return
        }

        if (Number.isNaN(likeCounter.innerText)) {
            likeCounter.innerText = 0
        }

        likeCounter.innerText = Number.parseInt(likeCounter.innerText, 10) - 1

        // In case count is calculated wrong, don't go below 0 likes
        if (likeCounter.innerText <= 0) {
            likeCounter.innerText = "Gefällt mir"
        }
    }

    handleLikeRejected(/** HTMLElement */ likeButton, /** Error */ reason) {
        /** void */
        if (reason.message === "") {
            return
        }

        openGenericModal(reason.message, "Jetzt Beitrag liken")

        likeButton.disabled = false
    }

    mapErrorResponse(/** string **/ starName, /** Response */ response) {
        /** string */
        const mappingKey = response.message || response.error_message || "Es ist ein unbekannter Fehler aufgetreten"

        switch (mappingKey) {
            case "no post found": {
                const postId = response["post-id"] || null
                if (postId) {
                    document.getElementById(`post-${postId}`).remove()
                }

                return `Leider wurde dieser Beitrag in der Zwischenzeit entfernt. ${starName} freut sich über deinen Like bei einem anderen Beitrag`
            }

            case "userId is missing":
            case "postId is missing":
            case "creatorId is missing":
                return "Bei dieser Aktion ist ein Problem aufgetreten. Lade die Seite neu und versuche es bitte erneut."

            case "add like failed":
            case "revoke like failed":
                return "Etwas ist schiefgelaufen. Falls dieses Problem längere Zeit bestehen bleibt, dann wende dich bitte an den technischen Support."

            case "unknown type":
                return "Die gewünschte Aktion wurde ausgeführt, ist allerdings von einem unbekannten Typ. Bitte lade die Seite neu."

            case "not logged in":
                return "Wir konnten nicht sicherstellen, dass du angemeldet bist. Bitte lade die Seite neu und versuche es noch einmal."

            default:
                return "Etwas ist schiefgelaufen. Falls dieses Problem längere Zeit bestehen bleibt, dann wende dich bitte an den technischen Support."
        }
    }

    preparePostBody(/** HTMLElement */ likeButton) {
        /** FormData */
        if (typeof userData === "undefined" || !userData.userId) {
            throw new Error(
                "Wir können deinen Benutzerstatus nicht ermitteln. Versuche bitte die Seite neuzuladen oder dich neu anzumelden.",
            )
        }

        if (!("postId" in likeButton.dataset)) {
            throw new Error(
                "Etwas ist beim Auslesen der ID des Posts schiefgegangen. Bitte lade die Seite neu und versuche es noch einmal.",
            )
        }

        const postContainer = likeButton.closest(".stars-post")
        const creatorData = postContainer.querySelector("data[data-owner-id]")

        if (!("ownerId" in creatorData.dataset)) {
            throw new Error(
                "Etwas ist beim Auslesen der ID des Posts schiefgegangen. Bitte lade die Seite neu und versuche es noch einmal.",
            )
        }

        const likeCounter = likeButton.querySelector(".like-count")
        const likesData = new FormData()

        let currentLikes

        if (likeCounter === null) {
            currentLikes = 0
        } else if (Number.isNaN(likeCounter.innerText)) {
            currentLikes = 0
        } else {
            currentLikes = Number.parseInt(likeCounter.innerText, 10)
        }

        likesData.set("userId", userData.userId)
        likesData.set("creatorId", creatorData.dataset.ownerId)
        likesData.set("postId", likeButton.dataset.postId)
        likesData.set("likesCount", currentLikes.toString())

        return likesData
    }

    isUnableToChangeLikeState(/** HTMLElement */ likeButton) {
        /** boolean */
        if (likeButton.hasAttribute("data-toggle")) {
            // Jump out if modals should be toggled because user is not logged in, not age verified or not a fan
            return true
        }

        if (typeof userData === "undefined" || userData.userId === undefined || userData.isLoggedIn === undefined) {
            InfoPanel.displayMessage(
                "Wir können deinen Benutzerstatus nicht ermitteln. Versuche bitte die Seite neuzuladen oder dich neu anzumelden.",
                InfoPanel.Type.Error,
            )
            return true
        }

        if (userData.userId === "0" || !userData.isLoggedIn) {
            // If we reach this point, personalisation didn't work, so try opening modal here
            LadiesMain.showLoginModal()
            return true
        }

        return !!likeButton.closest(".stars-post").querySelector('data[value="unpaid"]')
    }
}
